import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plane, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LanguageSelector } from "@/components/common/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { TwoFactorForm } from "@/components/auth/TwoFactorForm";
import { useST } from "@/i18n/securityText";

export const CustomerLogin = () => {
  const { t } = useTranslation();
  const { login, loginWithToken, isAuthenticated, isStaff, loading } = useAuth();
  const { st } = useST();
  const [challenge, setChallenge] = useState(null);
  const go = (user) => navigate(user?.role === "admin" || user?.role === "agent" ? "/portal" : from, { replace: true });
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const from = location.state?.from?.pathname || "/account";

  if (!loading && isAuthenticated) {
    return <Navigate to={isStaff ? "/portal" : "/account"} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const user = await login(email, password);
      if (user?.twoFactor) { setChallenge(user.challenge); return; }
      go(user);
    } catch (err) {
      setError(t("portal.login.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <div className="flex justify-end p-4">
        <LanguageSelector />
      </div>
      <div className="flex-1 grid place-items-center px-4">
        <div className="w-full max-w-sm">
          <div className="flex flex-col items-center mb-8">
            <div className="h-12 w-12 rounded-xl bg-venka-green-light/20 grid place-items-center mb-3">
              <Plane className="h-6 w-6 text-venka-green-light" />
            </div>
            <h1 className="text-xl font-bold">{t("account.login.title")}</h1>
            <p className="text-sm text-slate-400 mt-1">{t("account.login.subtitle")}</p>
          </div>
          {challenge ? (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
              <TwoFactorForm tone="green" challenge={challenge} onBack={() => { setChallenge(null); setPassword(""); }}
                onDone={(tok, u) => { loginWithToken(tok, u); go(u); }} />
            </div>
          ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-4 rounded-2xl border border-slate-800 bg-slate-900/60 p-6"
            data-testid="customer-login-form"
          >
            <div className="space-y-1.5">
              <Label htmlFor="email">{t("portal.login.email")}</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-slate-950 border-slate-700" data-testid="customer-login-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">{t("portal.login.password")}</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-slate-950 border-slate-700" data-testid="customer-login-password" />
            </div>
            {error && <p className="text-sm text-red-400" data-testid="customer-login-error">{error}</p>}
            <Button type="submit" disabled={submitting} className="w-full rounded-full bg-venka-green-light text-slate-950 hover:bg-venka-green-light font-semibold" data-testid="customer-login-submit">
              {submitting ? (<span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> {t("portal.login.signingIn")}</span>) : t("portal.login.submit")}
            </Button>
            <p className="text-center"><Link to="/forgot-password" className="text-sm text-venka-green-light hover:underline" data-testid="forgot-link">{st("forgot")}</Link></p>
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerLogin;
